import * as status from 'http-status';
import { REDUCER } from '../reducers/reducersConst';
import * as guestApi from '../services/api/guestApi';
import routerHistory from '../routerHistory';
import { ROUTES } from '../const';
import {
  getGuestProfileFromStorage,
  getGuestSessionFromStorage,
  guestTokenStorageHandler,
  removeGuestSessionFromStorage,
  removeGuestTokenFromStorage,
  setGuestProfileInStorage,
  setGuestSessionInStorage
} from '../shared';

export const GUEST_CODE_ERROR = {
  NOT_FOUND: 'notFound',
  EXPIRED: 'expired',
  INVALID_DATA: 'invalidData',
  GENERIC: 'generic'
};

/* SYNC ACTIONS ******************************/

export let isFetching = fetching => ({ type: REDUCER.GUEST.IS_FETCHING, isFetching: fetching });

export let setResolvedClient = resolvedClient => ({
  type: REDUCER.GUEST.SET_RESOLVED_CLIENT,
  resolvedClient
});

export let setCodeError = codeError => ({ type: REDUCER.GUEST.SET_CODE_ERROR, codeError });

export let setSession = session => ({ type: REDUCER.GUEST.SET_SESSION, session });

export let setSessionExpiredModal = isOpen => ({
  type: REDUCER.GUEST.SET_SESSION_EXPIRED_MODAL,
  sessionExpiredModalIsOpen: isOpen
});

export let clearGuestCodeError = () => setCodeError(null);

export let clearSession = () => ({ type: REDUCER.GUEST.CLEAR_SESSION });

/* API CALLS ******************************/

const errorToCodeError = error => {
  const httpStatus = error && error.response && error.response.status;
  if (httpStatus === status.GONE) {
    // classroom-be serializa el error como { code, data: { expiredAt, ... } } (ver GuestCodeExpiredError /
    // HttpError.toJSON en smart-classroom-be), no como { expiredAt } en la raíz.
    const errorData = error.response.data && error.response.data.data;
    return { type: GUEST_CODE_ERROR.EXPIRED, expiredAt: errorData && errorData.expiredAt };
  }
  if (httpStatus === status.NOT_FOUND) {
    return { type: GUEST_CODE_ERROR.NOT_FOUND };
  }
  if (httpStatus === status.BAD_REQUEST) {
    // validación Joi del payload en classroom-be (en la práctica, el email: Joi .email() rechaza TLDs desconocidos)
    return { type: GUEST_CODE_ERROR.INVALID_DATA };
  }
  return { type: GUEST_CODE_ERROR.GENERIC };
};

/**
 * Validate a guest code and resolve the client/school it belongs to
 * @param code
 * @returns {function(*)}
 */
export let validateGuestCode = code => {
  return dispatch => {
    dispatch(isFetching(true));
    dispatch(setCodeError(null));
    return guestApi
      .validateGuestCode(code)
      .then(res => {
        dispatch(setResolvedClient(res.data));
        dispatch(isFetching(false));
        return res.data;
      })
      .catch(error => {
        dispatch(setResolvedClient(null));
        dispatch(setCodeError(errorToCodeError(error)));
        dispatch(isFetching(false));
        return error;
      });
  };
};

/**
 * Create a guest session (short-lived guest token) for a validated code
 * @param code
 * @param name
 * @param email
 * @returns {function(*)}
 */
export let createGuestSession = (code, name = '', email = '') => {
  return dispatch => {
    dispatch(isFetching(true));
    return guestApi
      .createGuestSession(code, name, email)
      .then(res => {
        const { data } = res;
        guestTokenStorageHandler(data.token);
        setGuestSessionInStorage({
          code,
          clientId: data.clientId,
          schoolId: data.schoolId,
          clientName: data.clientName,
          expiresAt: data.expiresAt
        });
        if (name || email) {
          setGuestProfileInStorage({ name, email });
        }
        dispatch(setSession(data));
        dispatch(setSessionExpiredModal(false));
        dispatch(isFetching(false));
        return data;
      })
      .catch(error => {
        dispatch(setCodeError(errorToCodeError(error)));
        dispatch(isFetching(false));
        return error;
      });
  };
};

/**
 * Retry creating a guest session from the code stored in the current guest session, used
 * when the guest token expires mid-navigation ("Volver a entrar")
 * @returns {function(*)}
 */
export let reenterGuestSession = () => {
  return dispatch => {
    const storedSession = getGuestSessionFromStorage();
    if (!storedSession || !storedSession.code) {
      routerHistory.push(ROUTES.LOGINAUTH);
      return Promise.resolve();
    }
    const { name, email } = getGuestProfileFromStorage();
    return dispatch(createGuestSession(storedSession.code, name, email)).then(data => {
      if (!data || !data.token) {
        // no se pudo reabrir la sesión: volver a la landing con el código para que muestre el estado real
        // (caducado, no encontrado o formulario precargado con el perfil guardado)
        removeGuestTokenFromStorage();
        removeGuestSessionFromStorage();
        dispatch(clearSession());
        routerHistory.push(`${ROUTES.GUEST_LANDING}/${storedSession.code}`);
      }
      return data;
    });
  };
};

/**
 * Clear the guest session and go back to the login screen
 */
export let exitGuestSession = () => {
  return dispatch => {
    removeGuestTokenFromStorage();
    removeGuestSessionFromStorage();
    dispatch(clearSession());
    routerHistory.push(ROUTES.LOGINAUTH);
  };
};
